import { React, useState } from 'react';
import _get from 'lodash/get';
import { CustomColumn, IListConfigParams, StringColumn } from 'baseui/data-table';
import { CustomDateColumn, EMPTY_STRING, Label } from 'commons-ui';
import { Modal } from 'baseui/modal';
import { Show } from 'baseui/icon';

export const COL_NAMES = {
	_id: '_id',
	gameName: 'gameName',
	createdBy: 'createdBy',
	createdAt: 'createdAt',
	approvedBy: 'approvedBy',
	approvedAt: 'approvedAt',
	status: 'status',
	label: 'label',
	modifiedBy: 'modifiedBy',
	modifiedAt: 'modifiedAt',
	queryText: 'queryText',
	defaultQueryParams: 'defaultQueryParams',
	errorMessage: 'errorMessage',
	version: 'version',
	isActive: 'isActive'
};

export const AthenaCrudRequestStatus: { [key: number]: string } = {
	0: 'IN_PROGRESS',
	1: 'WAITING_FOR_APPROVAL',
	2: 'APPROVED',
	3: 'REJECTED',
	4: 'FAILED'
};

export const AthenaCrudRequestListConfig = () => {
	return [
		StringColumn({
			title: 'Game Name',
			minWidth: 100,
			maxWidth: 250,
			mapDataToValue: (data: any) => data[COL_NAMES.gameName] || EMPTY_STRING
		}),
		StringColumn({
			title: 'Label',
			minWidth: 100,
			maxWidth: 250,
			mapDataToValue: (data: any) => data[COL_NAMES.label] || EMPTY_STRING
		}),

		StringColumn({
			title: 'Status',
			maxWidth: 300,
			mapDataToValue: (data: any) => {
				const status = data[COL_NAMES.status];
				return AthenaCrudRequestStatus[status] || 'Unknown Status';
			}
		}),
		StringColumn({
			title: 'Version',
			maxWidth: 300,
			mapDataToValue: (data: any) => {
				const version = data[COL_NAMES.version].toString();
				return version || 'Unknown Version';
			}
		}),
		StringColumn({
			title: 'Active',
			maxWidth: 100,
			mapDataToValue: (data: any) => data[COL_NAMES.isActive].toString()
		}),
		CustomColumn({
			title: 'Query Text',
			maxWidth: 500,
			mapDataToValue: (data: any) => {
				return {
					desc: String(_get(data, COL_NAMES.queryText, EMPTY_STRING))
				};
			},
			renderCell: (params: IListConfigParams) => {
				const { value } = params;
				const truncatedDesc: string =
					value?.desc.split(' ').slice(0, 5).join(' ') + (value.desc.split(' ').length > 5 ? '...' : '');

				const [isModalOpen, setIsModalOpen] = useState(false);

				const handleClick = () => {
					setIsModalOpen(true);
				};

				return (
					<>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								height: '100%',
								gap: '8px'
							}}>
							<button
								style={{
									display: 'flex',
									alignItems: 'center',
									cursor: 'pointer',
									border: 'none',
									background: 'none',
									padding: '0'
								}}
								onClick={handleClick}
								type='button'>
								<Show size={15} />
							</button>
							<Label label={truncatedDesc} />
						</div>
						{isModalOpen && (
							<Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title='Query Text' size='lg'>
								<div style={{ padding: '36px' }}>
									<pre
										style={{
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											fontFamily: 'inherit',
											margin: 0
										}}>
										{value.desc}
									</pre>
								</div>
							</Modal>
						)}
					</>
				);
			}
		}),
		CustomColumn({
			title: 'defaultQueryParams',
			maxWidth: 300,
			mapDataToValue: (data: any) => JSON.stringify(data[COL_NAMES.defaultQueryParams]) || EMPTY_STRING,
			renderCell: (params: IListConfigParams) => {
				const { value } = params;
				const truncatedDesc: string = value?.split(' ').slice(0, 2).join(' ') + (value?.split(' ').length > 2 ? '...' : '');
				const [isModalOpen, setIsModalOpen] = useState(false);

				const handleClick = () => {
					setIsModalOpen(true);
				};

				return (
					<>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								height: '100%',
								gap: '8px'
							}}>
							<button
								style={{
									display: 'flex',
									alignItems: 'center',
									cursor: 'pointer',
									border: 'none',
									background: 'none',
									padding: '0'
								}}
								onClick={handleClick}
								type='button'>
								<Show size={15} />
							</button>
							<Label label={truncatedDesc} />
						</div>
						{isModalOpen && (
							<Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title='Default Query Params' size='lg'>
								<div style={{ padding: '36px' }}>
									<pre
										style={{
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											fontFamily: 'inherit',
											margin: 0
										}}>
										{value}
									</pre>
								</div>
							</Modal>
						)}
					</>
				);
			}
		}),
		StringColumn({
			title: 'Created By',
			minWidth: 200,
			maxWidth: 300,
			mapDataToValue: (data: any) => data[COL_NAMES.createdBy] || EMPTY_STRING
		}),
		CustomDateColumn({
			title: 'Created At',
			minWidth: 200,
			maxWidth: 300,
			columnName: COL_NAMES.createdAt
		}),
		StringColumn({
			title: 'Updated By',
			minWidth: 200,
			maxWidth: 300,
			mapDataToValue: (data: any) => data[COL_NAMES.modifiedBy] || EMPTY_STRING
		}),
		CustomDateColumn({
			title: 'Updated At',
			minWidth: 200,
			maxWidth: 300,
			columnName: COL_NAMES.modifiedAt
		}),
		StringColumn({
			title: 'Approved By',
			minWidth: 200,
			maxWidth: 300,
			mapDataToValue: (data: any) => data[COL_NAMES.approvedBy] || EMPTY_STRING
		}),
		CustomDateColumn({
			title: 'Approved At',
			minWidth: 200,
			maxWidth: 300,
			columnName: COL_NAMES.approvedAt || COL_NAMES.rejectedAt
		})
	];
};

export const DEFAULT_CONFIG: {
	pageDetails: {
		pageNo: number;
		pageSize: number;
	};
} = {
	pageDetails: {
		pageNo: 1,
		pageSize: 20
	}
};
